import React, { useEffect, useState } from 'react';
import Layout, { NAVBAR_ISMINIMIZED_KEY } from '../../components/Layout/Layout.component';
import { useStyles } from './Support.styles';
import { useStyles as mainStyles } from '../styles';

import { Grid } from '@material-ui/core';
import { ButtonOne } from '../../components/ButtonOne/ButtonOne.component';
import { AxiosResponse } from 'axios';
import { getContactData } from '../../services/support/contactData/contactData.service';
import { getInstructionsList } from '../../services/support/instructions/supportInstructions.service';
import { getMoviesList } from '../../services/supportMovie/supportMovie.service';
import {
  IInstructionListResponse,
  IInstructionListItem,
} from '../../services/support/instructions/supportInstructions.types';
import { IContactData } from '../../services/support/contactData/contactData.types';
import { EditContactData } from '../../components/Support/EditContactData/EditContactData.component';
import { InstructionCard } from '../../components/Support/Instructions/InstructionCard/InstructionCard.component';
import usePermission, { UserPermission } from '../../hooks/usePermission';
import useDocumentTitle from '../../hooks/DocumentTitle/documentTitle';
import { useTranslation } from 'react-i18next';
import EditIcon from '@material-ui/icons/Edit';
import HelpCenterOutlinedIcon from '@mui/icons-material/HelpCenterOutlined';
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import { Paths } from '../../utils/routerUrls';
import Breadcrumbs from '../../components/Breadcrumbs.component';
import { useHistory } from 'react-router';
import { IMovieListItem, IMovieListResponse } from '../../services/supportMovie/supportMovie.types';
import { MovieIframe } from '../../components/SupportMovie/MovieIframe/MovieIframe.component';
import { getMainPage } from '../../services/support/mainPage/mainPage.service';
import { IMainPage } from '../../services/support/mainPage/mainPage.types';
import { EditMainPage } from '../../components/Support/EditMainPage/EditMainPage.component';
import { sanitizeHtmlContent } from '../../utils/reactQuillUtils';
import { Link } from '@mui/material';
import { highContrastMode } from '../../utils/highContrastChecker';
import Typography from '@material-ui/core/Typography';

const Support: React.FC = () => {
  const [instructions, setInstructions] = useState<IInstructionListItem[]>([]);
  const [videos, setVideos] = useState<IMovieListItem[]>([]);
  const [showMoreInstructions, setShowMoreInstructions] = useState<boolean>(false);
  const [showMoreVideos, setShowMoreVideos] = useState<boolean>(false);

  const [isEditContactOpen, setIsEditContactOpen] = useState<boolean>(false);
  const [isEditMainPageOpen, setIsEditMainPageOpen] = useState<boolean>(false);

  const [contactData, setContactData] = useState<IContactData>({ description: '', contactInfo: '' });
  const [mainPage, setMainPage] = useState<IMainPage>({ htmlContent: '' });

  const classes = useStyles();
  const mainClasses = mainStyles();

  const history = useHistory();

  const isAdminPermission = usePermission(UserPermission.AdminSupportManage);
  const { t } = useTranslation();
  useDocumentTitle(`Centrum pomocy ${t('global:shortNames.beneficiary')} ${t('global:shortNames.system')}`);

  const switchShowMoreInstructions = () => {
    setShowMoreInstructions(!showMoreInstructions);
    history.push(Paths.supportInstructions);
  };
  const switchShowMoreVideos = () => {
    setShowMoreVideos(!showMoreVideos);
    history.push(Paths.supportMovies);
  };

  const handlePdfClick = () => {
    history.push(Paths.supportPdf);
  };

  const handleFaqClick = () => {
    history.push(Paths.supportFaq);
  };

  const handleMovieClick = (id: string) => {
    history.push(`${Paths.supportMoviesWatch}/${id}`);
  };

  const refreshContactData = () => {
    getContactData()
      .then((response: AxiosResponse<IContactData>) => {
        setContactData(response.data);
      })
      .catch(() => {
        console.error('error');
      });
  };

  const refreshMainPageData = () => {
    getMainPage()
      .then((response: AxiosResponse<IMainPage>) => {
        setMainPage(response.data);
      })
      .catch(() => {
        console.error('error');
      });
  };

  useEffect(() => {
    refreshContactData();
    refreshMainPageData();
  }, []);

  const initInstructions = () => {
    getInstructionsList()
      .then((response: AxiosResponse<IInstructionListResponse>) => {
        setInstructions(response.data.list.sort((x, y) => Number(y.isLiked) - Number(x.isLiked)));
      })
      .catch(() => {
        console.error('error');
      });
  };

  const initMovies = () => {
    getMoviesList()
      .then((response: AxiosResponse<IMovieListResponse>) => {
        setVideos(response.data.list.sort((x, y) => Number(y.isLiked) - Number(x.isLiked)));
      })
      .catch(() => {
        console.error('error');
      });
  };

  useEffect(() => {
    initInstructions();
    initMovies();
  }, []);

  const movieStyleProps = { width: '100%', height: '180px', border: 'none' };
  const borderForLinks: React.CSSProperties = highContrastMode
    ? { border: `1px solid white`, borderRadius: '5px' }
    : { border: 'none' };

  return (
    <>
      <Layout viewType={isAdminPermission ? 'admin' : 'fullwidthForExaminer'}>
        <div
          style={{
            width: '100%',
            overflow: 'scroll',
            padding: 30,
            paddingTop: 0,
            fontFamily: 'Roboto',
          }}
        >
          <div className={classes.breadcrumbHeader}>
            <Breadcrumbs
              listRoute={Paths.support}
              currentElement={`Centrum pomocy ${t('global:shortNames.beneficiary')} ${t('global:shortNames.system')}`}
            />
          </div>
          <Grid container style={{ marginBottom: 60 }}>
            <Grid item xs={12}>
              <Typography variant="h1" className={mainClasses.gridHeader} style={{ margin: '0px' }}>
                {t('support:header.mainHeader')}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <div className={classes.descriptionContainer}>
                <div
                  className={`ql-editor ${classes.qlEditor}`}
                  dangerouslySetInnerHTML={{ __html: sanitizeHtmlContent(mainPage.htmlContent) }}
                ></div>
              </div>
              {isAdminPermission && (
                <div className={classes.contactButtonLeft}>
                  <ButtonOne
                    variant="outlined"
                    color="primary"
                    aria-label="Edytuj"
                    onClick={() => setIsEditMainPageOpen(true)}
                  >
                    <EditIcon /> Edytuj
                  </ButtonOne>
                </div>
              )}
            </Grid>
            <Grid item xs={6}>
              <div className={classes.contactContainer}>
                <div className={`${classes.blueLayout} ${classes.contactData}`}>
                  <h2>{contactData.description}</h2>
                  <div>{contactData.contactInfo}</div>
                </div>
                {isAdminPermission && (
                  <div className={classes.contactButton}>
                    <ButtonOne
                      variant="outlined"
                      color="primary"
                      aria-label="Edytuj"
                      onClick={() => setIsEditContactOpen(true)}
                    >
                      <EditIcon /> Edytuj
                    </ButtonOne>
                  </div>
                )}
              </div>
            </Grid>
            <Grid item xs={12}>
              <hr />
            </Grid>
            <Grid item xs={12}>
              <h2 className={[classes.left, classes.h3].join(' ')}>Filmy wideo</h2>
              <Link
                component="button"
                onClick={switchShowMoreVideos}
                className={`${classes.rightWithMargin} ${classes.aWithoutHref}`}
              >
                Zobacz wszystkie filmy wideo
              </Link>
            </Grid>
            {(showMoreVideos
              ? videos
              : videos
                  .filter((item) => item.isLiked === true)
                  .sort((a, b) => (a.title.toLocaleLowerCase() > b.title.toLocaleLowerCase() ? 1 : -1))
            ).map((x) => (
              <Grid item xs={3} className={classes.linkPadding} key={x.id}>
                <MovieIframe movieUrl={x.url} movieStyle={movieStyleProps}></MovieIframe>
                <p
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    handleMovieClick(x.id);
                  }}
                >
                  <strong>{x.title}</strong>
                </p>
                <p className={classes.pMovie}>{x.description}</p>
              </Grid>
            ))}
            <Grid item xs={12}>
              <hr />
            </Grid>

            <Grid item xs={12}>
              <h2 className={[classes.left, classes.h3].join(' ')}>Instrukcje interaktywne</h2>
              <Link
                component="button"
                onClick={switchShowMoreInstructions}
                className={`${classes.rightWithMargin} ${classes.aWithoutHref}`}
              >
                Zobacz wszystkie instrukcje interaktywne
              </Link>
            </Grid>

            {(showMoreInstructions
              ? instructions
              : instructions
                  .filter((item) => item.isLiked === true)
                  .sort((a, b) => (a.title.toLocaleLowerCase() > b.title.toLocaleLowerCase() ? 1 : -1))
            ).map((instruction) => (
              <React.Fragment key={instruction.id}>
                <InstructionCard instructionProp={instruction} isEdit={false} />
              </React.Fragment>
            ))}

            <Grid item xs={12}>
              <hr />
            </Grid>

            <Grid item xs={12}>
              <h2 className={[classes.left, classes.h3].join(' ')}>Pozostałe linki</h2>
            </Grid>

            <Grid item xs={6} className={`${classes.linkPadding}`} style={borderForLinks}>
              <button
                className={`${classes.blueLayout} ${classes.linkContent} ${classes.blueBigButton}`}
                style={{ display: 'flex', alignItems: 'center' }}
                onClick={handleFaqClick}
              >
                <Grid item xs={10} style={{ textAlign: 'left', fontSize: '16px' }}>
                  <p>
                    <strong className={classes.blueBigButton}>FAQ</strong>
                  </p>
                  <p>Zbiór często zadawanych pytań i odpowiedzi</p>
                </Grid>
                <Grid item xs={2} style={{ textAlign: 'right' }}>
                  <HelpCenterOutlinedIcon className={classes.icon} />
                </Grid>
              </button>
            </Grid>

            <Grid item xs={6} className={`${classes.linkPadding}`} style={borderForLinks}>
              <button
                className={`${classes.blueLayout} ${classes.linkContent} ${classes.blueBigButton}`}
                style={{ display: 'flex', alignItems: 'center' }}
                onClick={handlePdfClick}
              >
                <Grid item xs={10} style={{ textAlign: 'left', fontSize: '16px' }}>
                  <p className={classes.blueBigButton}>
                    <strong>Podręczniki użytkownika</strong>
                  </p>
                  <p>Zbiór instrukcji użytkowników systemu</p>
                </Grid>
                <Grid item xs={2} style={{ textAlign: 'right' }}>
                  <p>
                    <AutoStoriesOutlinedIcon className={classes.icon} />
                  </p>
                </Grid>
              </button>
            </Grid>
          </Grid>

          {isEditContactOpen && (
            <EditContactData
              onClose={() => setIsEditContactOpen(false)}
              onChange={refreshContactData}
              contactDataProp={contactData}
            />
          )}
          {isEditMainPageOpen && (
            <EditMainPage
              onClose={() => setIsEditMainPageOpen(false)}
              onChange={refreshMainPageData}
              mainPageProp={mainPage}
            />
          )}
        </div>
      </Layout>
    </>
  );
};

export default Support;
